<template>
  <div class="formatted-read-more-text" :style="{ '--bg-color': backgroundColor }" :class="{ '-empty': valueEmpty }">
    <div
      class="formatted-read-more-text__text"
      :class="{ '-hidden': !moreVisible && !smallEnough }"
      :style="{ maxHeight: moreVisible ? `${textMaxHeight}px` : `${maxHeight}px` }"
    >
      <FormattedText
        ref="formattedText"
        :value="value"
        :placeholder="placeholder"
        :tag="tag"
        class="formatted-read-more-text__formatted-text"
      />
    </div>

    <button
      v-if="!smallEnough"
      type="button"
      class="formatted-read-more-text__button"
      :class="{ '-hidden': !moreVisible }"
      @click="toggleMore"
    >
      {{ moreVisible ? $t("labels.showLess") : $t("actions.readMore") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
import FormattedText from "./FormattedText.vue";
import { useResizeObserver } from "@vueuse/core";
import { isEmpty } from "@/helpers/utility/Text";
import { throttle } from "lodash-es";

const props = withDefaults(
  defineProps<{
    value?: string;
    placeholder?: string;
    tag?: string;
    maxHeight?: number;
    open?: boolean;
    backgroundColor?: string;
  }>(),
  {
    placeholder: "",
    tag: "div",
    maxHeight: 225,
    open: false,
    backgroundColor: "var(--color-background-deep)"
  }
);

const emit = defineEmits<{
  (e: "update:open", value: boolean): void;
}>();

const moreVisible = ref(props.open);
const textMaxHeight = ref(0);
const formattedText = ref<InstanceType<typeof FormattedText> | null>(null);

const smallEnough = computed(() => textMaxHeight.value <= props.maxHeight);

const valueEmpty = computed(() => isEmpty(props.value) && props.placeholder == "");

function toggleMore() {
  moreVisible.value = !moreVisible.value;
  emit("update:open", moreVisible.value);
}

watch(
  () => props.open,
  (value) => {
    moreVisible.value = value;
  }
);
const throttledUpdate = throttle(() => {
  textMaxHeight.value = formattedText.value?.el?.clientHeight ?? 0;
  console.log("update height", textMaxHeight.value);
}, 500, { trailing: true });

onMounted(() => {
  textMaxHeight.value = formattedText.value?.el?.clientHeight ?? 0;
});
useResizeObserver(() => formattedText.value?.el, () => {
  throttledUpdate();
});
</script>

<style lang="scss">
.formatted-read-more-text {
  &__button {
    display: flex;
    width: 100%;
    background-color: transparent;
    text-transform: uppercase;
    border: none;
    text-align: center;
    font-family: var(--font-family-ui);
    color: var(--color-text-medium);
    font-weight: 600;
    padding: 1rem 0;
    transition: color 300ms, transform 300ms;
    white-space: nowrap;
    align-items: center;
    gap: 0.5rem;

    &:hover {
      color: var(--color-text);
    }

    &.-hidden {
      transform: translateY(-0.5rem);
    }

    &::before,
    &::after {
      content: '';
      display: block;
      background-color: var(--color-border-light);
      height: 1px;
      width: 100%;
    }
  }

  &__text {
    position: relative;
    overflow: hidden;
    transition: max-height 0.5s;

    &.-hidden {
      &::after {
        content: "";
        display: block;
        position: absolute;
        bottom: 0;
        right: 0;
        left: 0;
        height: 4rem;
        background-image: linear-gradient(
          to bottom,
          rgba(255, 255, 255, 0),
          var(--bg-color)
        );
      }
    }
  }
  &__formatted-text > *:first-child {
    margin-top: 0 !important;
  }

  &.-empty {
    display: none;
  }
}
</style>
